import { LupaIcon } from "assets/icons/LupaIcon";
import { showErrorToast } from "components/CustomAlerts/CustomToast";
import { Input } from "components/Input/Input";
import { Pagination } from "components/Pagination/Pagination";
import { Select } from "components/Select/Select";
import { DEFAULT_LIMIT_PAGINATION } from "constants/appConstants";
import { useFormik } from "formik";
import { NormativaI } from "interfaces/Normativa";
import { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getNormativas } from "services/normativas";
import {
  fetchAlcances,
  fetchTiposEfecto,
  fetchTiposResoluciones,
} from "store/slices/appSlice";
import { AppDispatch, RootState } from "store/store";
import { NormativasABM } from "./NormativasABM/NormativasABM";
import { NormativasRowActions } from "./NormativasTable/NormativasRowActions";
import { NormativasTable } from "./NormativasTable/NormativasTable";

export const Normativas = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [elementos, setElementos] = useState<NormativaI[]>();
  const [cantidad, setCantidad] = useState<number>();
  const [page, setPage] = useState<number>();
  const [limit, setLimit] = useState<number>();

  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { tiposNormativa, tiposEfecto, alcances, loadingAppData } = useSelector(
    (st: RootState) => st.appData,
  );

  const [showModalABM, setShowModalABM] = useState(false);
  const [actionType, setActionType] = useState<"create" | "edit" | undefined>();
  const [selectedElement, setSelectedElement] = useState<
    NormativaI | undefined
  >();

  const initialValues = {
    tipo_normativa_id: "",
    tipo_efecto_id: "",
    estadoNotificacion: "",
    alcance_id: "",
    nombre: "",
    descripcion: "",
  };

  const { values, handleChange, resetForm } = useFormik({
    initialValues,
    onSubmit: () => {},
  });

  const searchResoluciones = async (params?: {
    filtros?: { [key: string]: any };
  }) => {
    try {
      setIsLoading(true);
      const response = await getNormativas({ ...params?.filtros });
      setElementos(response.elementos);
      setCantidad(response.cantidad);
      setPage(response.page);
      setLimit(params?.filtros?.limit ?? DEFAULT_LIMIT_PAGINATION);
    } catch (err) {
      showErrorToast("No fue posible cargar las normativas");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModalABM(false);
    setTimeout(() => setActionType(undefined), 500);
  };

  const handleEdit = (elemento: NormativaI) => {
    setActionType("edit");
    setSelectedElement(elemento);
    setShowModalABM(true);
  };

  const handleCreate = () => {
    setActionType("create");
    setShowModalABM(true);
  };

  const handleChangePagination = (newFiltros: {
    [key: string]: string | number;
  }) => {
    searchResoluciones({ filtros: { ...values, ...newFiltros } });
  };

  const handleClearFilters = () => {
    resetForm();
    searchResoluciones();
  };

  const handleChangeWithResetPage = (pageOne: any) => {
    setPage(1);
    handleChange(pageOne);
  };

  const refreshTable = () => {
    setPage(1);
    searchResoluciones({ filtros: { ...values, page: 1 } });
  };

  const renderActions = (
    elemento: NormativaI,
    onClickShowDetail: (elemento: NormativaI) => void,
  ) => {
    return (
      <NormativasRowActions
        elemento={elemento}
        onClickEdit={handleEdit}
        refreshTable={refreshTable}
        onClickShowDetail={onClickShowDetail}
      />
    );
  };

  useEffect(() => {
    searchResoluciones({ filtros: { ...values, limit, page } });
  }, [values]);

  useEffect(() => {
    if (!tiposNormativa) dispatch(fetchTiposResoluciones());
    if (!tiposEfecto) dispatch(fetchTiposEfecto());
    if (!alcances) dispatch(fetchAlcances());
  }, []);

  return (
    <section>
      <h1 className="section-title">Normativas</h1>

      <h2 className="section-subtitle mt-2">
        Desde acá podrás gestionar las normativas.
      </h2>

      <div className="d-flex justify-content-between mt-4 mb-3">
        <div className="d-flex column-gap-3">
          <Input
            size="sm"
            id="nombre"
            onChange={handleChangeWithResetPage}
            placeholder="Buscar nombre"
            values={values}
            startIcon={<LupaIcon />}
          />
          <Input
            size="sm"
            id="descripcion"
            onChange={handleChangeWithResetPage}
            placeholder="Buscar descripción"
            values={values}
            startIcon={<LupaIcon />}
          />
          <Select
            id="tipo_normativa_id"
            values={values}
            onChange={handleChangeWithResetPage}
            optionsFormatConfig={{ value: "id", label: "nombre" }}
            options={tiposNormativa ?? []}
            isLoading={loadingAppData.tiposNormativa}
            placeholder="Tipo"
            width="150px"
          />
          <Select
            id="tipo_efecto_id"
            values={values}
            onChange={handleChangeWithResetPage}
            optionsFormatConfig={{ value: "id", label: "nombre" }}
            options={tiposEfecto ?? []}
            isLoading={loadingAppData.tiposEfecto}
            placeholder="Efecto"
            width="145px"
          />
          <Select
            id="alcance_id"
            values={values}
            onChange={handleChangeWithResetPage}
            optionsFormatConfig={{ value: "id", label: "nombre" }}
            options={alcances ?? []}
            isLoading={loadingAppData.alcances}
            placeholder="Alcance"
            width="145px"
          />
          <Button
            size="sm"
            variant="secondary"
            className="clear-filters-button"
            onClick={handleClearFilters}
            disabled={
              !values.tipo_efecto_id &&
              !values.tipo_normativa_id &&
              !values.alcance_id &&
              !values.nombre &&
              !values.descripcion
            }
          >
            Limpiar filtros
          </Button>
        </div>
        <Button size="sm" onClick={handleCreate}>
          Nueva normativa
        </Button>
        <NormativasABM
          showModal={showModalABM}
          actionType={actionType}
          onClose={handleCloseModal}
          elemento={selectedElement}
          onSuccess={refreshTable}
        />
      </div>

      {isLoading && (
        <div className="d-flex justify-content-center w-full">
          <Spinner />
        </div>
      )}

      {!isLoading && elementos && cantidad != undefined && (
        <>
          <NormativasTable
            elementos={elementos}
            renderActions={renderActions}
          />
          <Pagination
            totalItems={cantidad}
            currentPage={page}
            currentLimit={limit}
            onChangePage={handleChangePagination}
            showPaginationOnlyOnMoreThanPages={1}
          />
        </>
      )}
    </section>
  );
};
